import React, { createContext, useContext, useEffect, useState, useRef } from 'react';

export type ThemeMode = 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextType {
  theme: ThemeMode;
  resolvedTheme: ResolvedTheme;
  toggleTheme: () => void;
  setTheme: (theme: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

// Enables smooth CSS transition on all elements during theme change
export const triggerSmoothThemeTransition = () => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.add('theme-transition');
  window.clearTimeout((window as any).__themeTransitionTimer);
  (window as any).__themeTransitionTimer = window.setTimeout(() => {
    root.classList.remove('theme-transition');
  }, 500);
};

import { StatusBar, Style } from '@capacitor/status-bar';

const syncNativeStatusBar = async (currentTheme: ThemeMode) => {
  if (typeof window === 'undefined') return;
  try {
    const isNative = !!(window as any).Capacitor?.isNativePlatform?.();
    if (!isNative) return;

    await StatusBar.show().catch(() => {});
    await StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});
    await StatusBar.setBackgroundColor({ color: '#00000000' }).catch(() => {});

    if (currentTheme === 'dark') {
      await StatusBar.setStyle({ style: Style.Dark });
    } else {
      await StatusBar.setStyle({ style: Style.Light });
    }
  } catch {
    // ignore on web
  }
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = (localStorage.getItem('crevvi-theme') || localStorage.getItem('theme')) as ThemeMode;
      if (savedTheme === 'dark') return 'dark';
      try {
        localStorage.setItem('crevvi-theme', 'dark');
        localStorage.setItem('theme', 'dark');
      } catch {}
    }
    return 'dark';
  });

  const isInitialMount = useRef(true);

  const applyThemeToDOM = (newTheme: ThemeMode) => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(newTheme);
    try {
      localStorage.setItem('crevvi-theme', newTheme);
      localStorage.setItem('theme', newTheme);
    } catch {}
    syncNativeStatusBar(newTheme);
  };

  const applyThemeWithTransition = (newTheme: ThemeMode) => {
    setThemeState(newTheme);

    // If reduced motion is requested or initial mount, apply directly
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (isInitialMount.current || prefersReducedMotion) {
      applyThemeToDOM(newTheme);
      isInitialMount.current = false;
      return;
    }

    // If View Transitions API is supported, run full-page hardware-accelerated cross-fade transition
    if (typeof document !== 'undefined' && 'startViewTransition' in document) {
      try {
        (document as any).startViewTransition(() => {
          applyThemeToDOM(newTheme);
        });
        return;
      } catch {
        // fallback to standard DOM update below
      }
    }

    // Fallback for browsers/webviews without View Transitions API
    triggerSmoothThemeTransition();
    applyThemeToDOM(newTheme);
  };

  // Initial mount application
  useEffect(() => {
    applyThemeToDOM(theme);
    isInitialMount.current = false;
  }, []);

  const toggleTheme = () => {
    const nextTheme: ThemeMode = theme === 'dark' ? 'light' : 'dark';
    applyThemeWithTransition(nextTheme);
  };

  const setTheme = (newTheme: ThemeMode) => {
    applyThemeWithTransition(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme: theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};